import { z } from 'zod';
import { parsePageAction, parseSlotAction } from '../hotbar/slotActions';

/**
 * UserPrefs — the canonical client-side user preference blob.
 *
 * Persisted under a single localStorage key (`helix_user_prefs`) so we can
 * round-trip the whole shape in one Zod parse. Each slice carries its own
 * `schemaVersion` so day-to-day field evolution can reset just the affected
 * slice instead of nuking every `helix_*` key (Phase F gain).
 *
 * The top-level `schemaVersion` remains as the nuclear option for emergency
 * full wipes (e.g. JWT format change). For routine schema evolution use the
 * per-slice path: bump the slice's `*_SCHEMA_VERSION` const and add a
 * migration entry to `SLICE_MIGRATIONS`.
 *
 * Volume fields are 0-1 floats (canonical form). UI sliders display 0-100;
 * the conversion happens at the read/write boundary, not in the schema.
 */

export const USER_PREFS_SCHEMA_VERSION = 1;

export const AUDIO_SCHEMA_VERSION = 1;
export const CONTROLS_SCHEMA_VERSION = 2;
export const UI_SCHEMA_VERSION = 2;
export const DEV_SCHEMA_VERSION = 6;
export const ACCESSIBILITY_SCHEMA_VERSION = 1;

const Volume = z.number().min(0).max(1);

const AudioPrefsSchema = z.object({
  schemaVersion: z.number().int().default(AUDIO_SCHEMA_VERSION),
  masterVolume: Volume.default(0.5),
  musicVolume: Volume.default(0.3),
  sfxVolume: Volume.default(0.7),
  muted: z.boolean().default(false),
});
const AUDIO_DEFAULTS = AudioPrefsSchema.parse({});

// Key bindings are not here: they live in KeyBindingManager (`helix_keybindings`)
// and on the character (modular bars plan, phase 3). An old `keybindings` field
// that nothing read was retired; Zod strips it from stored blobs.
// Controller remapping (controls plan C1, the next program's E5): what each shoulder and trigger
// fires, a `bar<B>_slot<S>` or `bar<B>_page_*` action. With a shift bumper chosen, that bumper fires
// nothing itself and, held, switches the other three to their shifted actions (bar 2 by default),
// as Shift does on the keyboard. An unknown action falls back to its default.
const padAction = (fallback: string) =>
  z.string().refine((a) => parseSlotAction(a) !== null || parsePageAction(a) !== null).catch(fallback).default(fallback);
const padButtons = (bar: number) => z.object({
  ability1: padAction(`bar${bar}_slot1`), ability2: padAction(`bar${bar}_slot2`),
  ability3: padAction(`bar${bar}_slot3`), ability4: padAction(`bar${bar}_slot4`),
});
const ControlsPrefsSchema = z.object({
  schemaVersion: z.number().int().default(CONTROLS_SCHEMA_VERSION),
  facingDirectionOnly: z.boolean().default(false),
  gamepadButtons: padButtons(1).catch(padButtons(1).parse({})).default(padButtons(1).parse({})),
  gamepadShiftedButtons: padButtons(2).catch(padButtons(2).parse({})).default(padButtons(2).parse({})),
  gamepadShiftButton: z.enum(['none', 'ability1', 'ability2']).catch('none').default('none'),
});
const CONTROLS_DEFAULTS = ControlsPrefsSchema.parse({});

const UiPrefsSchema = z.object({
  schemaVersion: z.number().int().default(UI_SCHEMA_VERSION),
  chatOpacity: z.number().min(0).max(1).default(0.85),
  showFps: z.boolean().default(false),
  // Floating combat text shows the source ability name under each damage
  // number ("30 / Fireball", DoT ticks as "Burn (tick)"). QA-driven
  // (2026-06-12: ambiguous 30+22 fireball frames) but user-facing — ui
  // slice. Default on; toggle in Settings → Interface.
  fctAbilityNames: z.boolean().default(true),
  // Hotbar slots show a seconds countdown over the cooldown sweep (DJ
  // 2026-10-07: "add a text timer on it, or at least as an option"). Default
  // on; toggle in Settings → Interface. Never shown for the global cooldown.
  hotbarCooldownNumbers: z.boolean().default(true),
  // Overhead names in a crowd (plan 15 §2.3, Settings → Interface): `all`
  // shows every name (decluttered, never faded); `smart` (default) keeps the
  // target, bosses, self and party, and shows everyone else when hovered or
  // when nothing overlaps them; `target` names only the target, the hovered
  // entity, bosses and self. Bars show in every mode.
  overheadNames: z.enum(['all', 'smart', 'target']).default('smart'),
  // Other players' overhead names in their calling's class colour (plan 23
  // row 34, lane BO; Settings → Interface). Default OFF: a red warrior name
  // read as "hostile".
  classTintNames: z.boolean().default(false),
  // Modular HUD theme / template (plan 17 §5, H1 decision 2026-10-25):
  // 'mix' (default: A leather frame & bars + C vine tracker),
  // 'a' (All A — Leather & Gilt), 'b' (All B — Parchment Slips),
  // 'c' (All C — Ink & Vine), 'classic' (Original Dark Ink & Brass).
  hudStyle: z.enum(['mix', 'a', 'b', 'c', 'classic']).default('mix'),
  // Tome vibe palette on the HUD (H2 decision 2026-10-25):
  // 'auto' follows the zone via zoneTomeVibe; or pin to a specific vibe palette.
  hudVibe: z.enum(['auto', 'default', 'grove', 'moonfall', 'harbour']).default('auto'),
});
const UI_DEFAULTS = UiPrefsSchema.parse({});

const DevPrefsSchema = z.object({
  schemaVersion: z.number().int().default(DEV_SCHEMA_VERSION),
  godMode: z.boolean().default(false),
  noclip: z.boolean().default(false),
  // The multiplayer debug-coordinate overlay (LOCAL <sid> / S(x,y) / C(x,y)).
  // dev slice, not ui — devConfig classifies it diagnostics, not user-facing
  // nameplates. Default FALSE since 2026-09-28 (U4): the labels are opt-in on
  // every surface, including Playwright (window.playwrightTest no longer
  // enables them). Explicit opt-ins: the Debug Toolbox 🏷️ toggle (writes this
  // pref), the `?debug=1` / window.forceDebug session seed, and
  // `?diag=1`/`?diagAll=1` (force ON over everything). The dev migration
  // 5 -> 6 also resets stored legacy `true` values once, so pre-existing
  // blobs that inherited the old default cannot keep the overlay on.
  showMultiplayerLabels: z.boolean().default(false),
  // When true, client freezes render a persistent center-screen popup listing
  // the freeze attribution + recent netcode events (instead of the default 1.8s
  // corner toast).
  //
  // OFF by default since 2026-07-26. It was on during the BIG-FREEZE-01 hunt so
  // freezes were immediately visible, but that investigation closed: the ~11s
  // metronome was a GNOME Shell extension blocking Mutter's main loop, not the
  // game. What remained was popups firing for `visible-unfocused` events —
  // Chrome throttling a tab that simply is not focused — which interrupted DJ
  // every time he alt-tabbed away to type. Freezes still record durably to the
  // server DomainLog (H2000_DOMAIN_LOG_DIR), so nothing is lost by defaulting
  // this off. Re-enable in Settings -> Interface, the launcher toggle, or
  // ?freezePopup=1.
  persistentFreezePopup: z.boolean().default(false),
  // The subtle bottom-right corner tag ("⏱ freeze 166ms · client") that flashes
  // for 1.8s whenever FreezeSentinel trips. Sibling of the popup above: that one
  // is the full center-screen card, this one is the glance-level tag.
  //
  // OFF by default since 2026-07-26, for the same reason and one more. The
  // BIG-FREEZE-01 hunt is closed (a GNOME Shell extension, not the game), so the
  // always-on tag no longer earns its interruption. The remaining regular firing
  // is the known ~170ms at-load `presentation-stall`, which pops a tag on every
  // single page load — including every VQA capture, where it lands in the corner
  // of screenshots taken for unrelated reasons.
  //
  // Unlike persistentFreezePopup this does NOT auto-enable on diagnostic
  // surfaces: /debug and /combatlab are exactly where captures are taken, so a
  // surface-based default would keep the noise precisely where it hurts. The
  // pref is the authority; ?freezeToast=1 turns it on for a session.
  //
  // Detection is untouched. Freezes still record durably to the server DomainLog
  // and remain queryable via dj_h2000_attribute_at — this hides a visual only.
  freezeCornerToast: z.boolean().default(false),
});
const DEV_DEFAULTS = DevPrefsSchema.parse({});

// Accessibility — the seam every new VFX/atmosphere layer reads to respect
// reduced-motion / photosensitivity. The public QA tunnel is aimed at
// streamers + random visitors, so this is a real-strangers concern, not a
// nicety. reduceMotion 'auto' defers to the OS prefers-reduced-motion query;
// shakeIntensity is a 0..1 camera-shake scalar; disableScreenFlash suppresses
// full-screen combat flashes; colorblindSafe swaps the red/green combat palette.
const AccessibilityPrefsSchema = z.object({
  schemaVersion: z.number().int().default(ACCESSIBILITY_SCHEMA_VERSION),
  reduceMotion: z.enum(['auto', 'on', 'off']).default('auto'),
  disableScreenFlash: z.boolean().default(false),
  shakeIntensity: z.number().min(0).max(1).default(1),
  colorblindSafe: z.boolean().default(false),
});
const ACCESSIBILITY_DEFAULTS = AccessibilityPrefsSchema.parse({});

export const UserPrefsSchema = z.object({
  schemaVersion: z.number().int().default(USER_PREFS_SCHEMA_VERSION),
  audio: AudioPrefsSchema.default(AUDIO_DEFAULTS),
  controls: ControlsPrefsSchema.default(CONTROLS_DEFAULTS),
  ui: UiPrefsSchema.default(UI_DEFAULTS),
  dev: DevPrefsSchema.default(DEV_DEFAULTS),
  accessibility: AccessibilityPrefsSchema.default(ACCESSIBILITY_DEFAULTS),
});

export type UserPrefs = z.infer<typeof UserPrefsSchema>;
export type AudioPrefs = UserPrefs['audio'];
export type ControlsPrefs = UserPrefs['controls'];
export type UiPrefs = UserPrefs['ui'];
export type DevPrefs = UserPrefs['dev'];
export type AccessibilityPrefs = UserPrefs['accessibility'];

export const USER_PREFS_DEFAULTS: UserPrefs = UserPrefsSchema.parse({});

// =============================================================================
// Per-slice migrations
// =============================================================================

/**
 * Slice-name → expected schema version. Drives the per-slice reset in
 * `parseUserPrefs` — if a stored slice's version is missing or older AND
 * no migration entry exists for that step, the slice resets to defaults
 * instead of failing the whole parse.
 *
 * Adding a migration:
 *   1. Bump the slice's `*_SCHEMA_VERSION` const.
 *   2. Add `{ from: oldVer, to: newVer, migrate: (raw) => ... }` to
 *      `SLICE_MIGRATIONS[slice]`. Return null to fall through to defaults.
 */
const SLICE_VERSIONS: Record<keyof Omit<UserPrefs, 'schemaVersion'>, number> = {
  audio: AUDIO_SCHEMA_VERSION,
  controls: CONTROLS_SCHEMA_VERSION,
  ui: UI_SCHEMA_VERSION,
  dev: DEV_SCHEMA_VERSION,
  accessibility: ACCESSIBILITY_SCHEMA_VERSION,
};

interface MigrationStep {
  from: number;
  to: number;
  /** Return the migrated slice or null to reset to defaults. */
  migrate: (raw: unknown) => unknown | null;
}

 
const SLICE_MIGRATIONS: Record<string, MigrationStep[]> = {
  audio: [],
  controls: [
    {
      from: 1,
      to: 2,
      migrate: (raw) => {
        if (!raw || typeof raw !== 'object') return null;
        return {
          ...(raw as Record<string, unknown>),
          schemaVersion: 2,
          facingDirectionOnly: false,
        };
      },
    },
  ],
  ui: [
    {
      from: 1,
      to: 2,
      migrate: (raw) => {
        if (!raw || typeof raw !== 'object') return null;
        return {
          ...(raw as Record<string, unknown>),
          schemaVersion: 2,
          hudStyle: 'mix',
          hudVibe: 'auto',
        };
      },
    },
  ],
  dev: [
    {
      from: 1,
      to: 2,
      migrate: (raw) => {
        if (!raw || typeof raw !== 'object') return null;
        return {
          ...(raw as Record<string, unknown>),
          schemaVersion: 2,
          persistentFreezePopup: false,
        };
      },
    },
    {
      from: 2,
      to: 3,
      migrate: (raw) => {
        if (!raw || typeof raw !== 'object') return null;
        return {
          ...(raw as Record<string, unknown>),
          schemaVersion: 3,
          persistentFreezePopup: true,
        };
      },
    },
    {
      // BIG-FREEZE-01 closed (root cause was outside the game). Turn the popup
      // back off for everyone who was migrated ON by step 2->3, so existing
      // stored prefs match the new default instead of keeping a hunt-era
      // setting forever.
      from: 3,
      to: 4,
      migrate: (raw) => {
        if (!raw || typeof raw !== 'object') return null;
        return {
          ...(raw as Record<string, unknown>),
          schemaVersion: 4,
          persistentFreezePopup: false,
        };
      },
    },
    {
      // The corner freeze tag becomes an explicit, stored preference instead of
      // an unconditional always-on visual. Seed it OFF so nobody inherits the
      // hunt-era noise; the toggle lives beside the popup's.
      from: 4,
      to: 5,
      migrate: (raw) => {
        if (!raw || typeof raw !== 'object') return null;
        return {
          ...(raw as Record<string, unknown>),
          schemaVersion: 5,
          freezeCornerToast: false,
        };
      },
    },
    {
      // 2026-09-28 (U4): the multiplayer debug-label overlay becomes opt-in.
      // The pref spent its whole life defaulting true, so any stored dev slice
      // carries `showMultiplayerLabels: true` by inheritance rather than by
      // choice — reset it once so stored prefs match the new OFF default.
      // (Same shape as the 3 -> 4 persistentFreezePopup reset above; a later
      // deliberate 🏷️ opt-in at version 6 is persisted and never re-reset.)
      from: 5,
      to: 6,
      migrate: (raw) => {
        if (!raw || typeof raw !== 'object') return null;
        return {
          ...(raw as Record<string, unknown>),
          schemaVersion: 6,
          showMultiplayerLabels: false,
        };
      },
    },
  ],
  accessibility: [],
};

function migrateSlice(sliceName: string, raw: unknown): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const expected = SLICE_VERSIONS[sliceName as keyof typeof SLICE_VERSIONS];
  const rawVer = (raw as { schemaVersion?: number }).schemaVersion;
  // Missing version stamp = pre-versioning data (Phase B blob). Treat it as
  // the current version so the user keeps their values; future bumps that
  // change fields will need an explicit 1 → 2 migration.
  const stored = rawVer ?? expected;
  if (stored === expected) return raw;
  if (stored > expected) return null; // future-versioned data — reset
  // Walk the migration ladder; bail if a step is missing
  const steps = SLICE_MIGRATIONS[sliceName] ?? [];
  let current: unknown = raw;
  let version = stored;
  while (version < expected) {
    const step = steps.find((s) => s.from === version);
    if (!step) return null; // no path forward → reset slice
    current = step.migrate(current);
    if (current === null) return null;
    version = step.to;
  }
  return current;
}

/**
 * Parse a raw value (from localStorage or anywhere) into UserPrefs.
 * Never throws — invalid input returns full defaults. Per-slice schema
 * mismatches reset only the affected slice; valid slices survive.
 */
export function parseUserPrefs(raw: unknown): UserPrefs {
  if (!raw || typeof raw !== 'object') return USER_PREFS_DEFAULTS;
  const obj = raw as Record<string, unknown>;
  const migrated: Record<string, unknown> = { schemaVersion: obj.schemaVersion };
  for (const slice of Object.keys(SLICE_VERSIONS)) {
    const migratedSlice = migrateSlice(slice, obj[slice]);
    if (migratedSlice !== null) migrated[slice] = migratedSlice;
    // null → omitted → AudioPrefsSchema.default() fills in
  }
  const result = UserPrefsSchema.safeParse(migrated);
  return result.success ? result.data : USER_PREFS_DEFAULTS;
}
